import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { DaemonRecord } from '../../../generated/api/types.gen.js';
import { Badge, Card, QueryBoundary, StatCard } from '../../../components/ui/index.js';
import type { BadgeVariant } from '../../../components/ui/index.js';
import { humanize } from '../admin-format.js';
import { daemonReads } from './daemon-queries.js';
import { useDaemonScope } from './DaemonScopeContext.js';

function formatRun(run: Record<string, unknown> | null): string {
  if (!run) return 'No run recorded';
  const status = typeof run.status === 'string' ? humanize(run.status) : 'Unknown';
  const startedAt = typeof run.startedAt === 'string' ? `, ${new Date(run.startedAt).toLocaleDateString()}` : '';
  return `${status}${startedAt}`;
}

function severityVariant(severity: string): BadgeVariant {
  return severity === 'critical' || severity === 'high' ? 'danger' : 'warning';
}

export const DaemonHealthTab: React.FC<{ daemon: DaemonRecord }> = ({ daemon }) => {
  const { scope } = useDaemonScope();
  const query = useQuery({ ...daemonReads(scope).readiness(daemon.id), refetchInterval: 15_000 });

  return (
    <QueryBoundary query={query} label="daemon health">
      {({ data }) => (
        <div className="space-y-5">
          <Card
            title="Automation readiness"
            actions={
              <Badge variant={data.ready ? 'success' : 'danger'} dot>
                {data.ready ? 'Ready' : `${data.blocking.length} blocking`}
              </Badge>
            }
          >
            <p className="mb-4 text-sm text-muted">
              {data.ready
                ? 'This daemon can be enabled and scheduled.'
                : 'Resolve the blocking checks before enabling automation.'}
            </p>
            <ul className="grid gap-2">
              {data.checks.map((check) => (
                <li key={check.key} className="flex items-start gap-3 rounded-lg border bg-surface-alt px-3 py-2">
                  {check.ok ? (
                    <CheckCircle2 size={16} className="mt-0.5 text-success" aria-hidden="true" />
                  ) : (
                    <AlertTriangle size={16} className="mt-0.5 text-warning" aria-hidden="true" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-heading">{humanize(check.key)}</span>
                      <Badge variant={check.ok ? 'success' : severityVariant(check.severity)}>
                        {check.ok ? 'Pass' : humanize(check.severity)}
                      </Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted">{check.message}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Self-review status">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <StatCard
                label="Open high or critical findings"
                value={data.selfReview.openHighFindings}
                tone={data.selfReview.openHighFindings > 0 ? 'danger' : undefined}
              />
              <StatCard label="Last security run" value={formatRun(data.selfReview.lastSecurityRun)} />
              <StatCard label="Last quality run" value={formatRun(data.selfReview.lastQualityRun)} />
            </div>
          </Card>

          <Card title="Permission grant usage">
            <p className="mb-3 text-sm text-muted">
              Active grants reduce repeated prompts, but long-lived scopes should stay visible and reversible.
            </p>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <StatCard
                label="Active grants"
                value={data.permissionGrantUsage.active}
                tone={data.permissionGrantUsage.active > 0 ? 'warning' : undefined}
              />
              <StatCard
                label="Allow always"
                value={data.permissionGrantUsage.allowAlways}
                tone={data.permissionGrantUsage.allowAlways > 0 ? 'warning' : undefined}
              />
              <StatCard label="Used in 7 days" value={data.permissionGrantUsage.usedLast7d} />
              <StatCard
                label="Stale long-lived"
                value={data.permissionGrantUsage.staleUnusedAlways ?? 0}
                tone={(data.permissionGrantUsage.staleUnusedAlways ?? 0) > 0 ? 'warning' : undefined}
              />
            </div>
          </Card>
        </div>
      )}
    </QueryBoundary>
  );
};
